import React from 'react'

import { Card, CardHeading, CardContent, CardFooter } from '@components/layout/Card'
import { RadarChart } from '@components/charts/RadarChart'

import { Grid } from '@UILibrary/Grid'
import { Text } from '@UILibrary/Text'

import { buildRadarChartDataProp } from './charts/data'
import { buildRadarChartOptionsProp } from './charts/options'
import FinancialHealthLineChart from '../shared/FinancialHealthLineChart'

import { Stat } from '../../../Shared/Stat'
import { LearnMore } from '../../../Shared/LearnMore'

interface ChartData {
  labels: string[]
  datasets: object
}

interface EmployeeInvestingProps {
  currentInvestmentPercentage: number
  averageSavings: number
  benchmark: number
  benchmarkVariancePercentage: number
  scopeVariancePercentage: number
  timeframe: string
  chartData: ChartData
  assetTypeDistribution: object[]
}

export const EmployeeInvesting: React.FC<EmployeeInvestingProps> = (props) => {
  return (
    <div id="employee-investing">
      <Grid cols="2" gap="s">
        <Card>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3" margin="none">
              How much of their income is your team saving every month?
            </Text>
            <Text body as="p" margin="none">
              A healthy income:savings ratio is considered to be above <strong>{props.benchmark}%</strong>.
            </Text>
          </CardHeading>
          <CardContent>
            <Text as="p" margin={{ bottom: 's' }}>
              Your team's average investing ratio is <strong>{props.currentInvestmentPercentage}%</strong>, which is <strong>{props.currentInvestmentPercentage < props.benchmark ? 'lower' : 'higher'}</strong> than the recommended minimum.
            </Text>
            <Grid cols="1-a" gap="m">
              <FinancialHealthLineChart
                height="medium"
                chartData={props.chartData}
                benchmark={props.benchmark}
              />
              <Stat percentage={props.scopeVariancePercentage} timeframe={props.timeframe} />
            </Grid>
            <Text bodySmall as="p" textAlign='center' margin='none'>
              (click legends to toggle on and off)
            </Text>
          </CardContent>
        </Card>

        <Card className='content-expand'>
          <CardHeading>
            <Text heading4a colour="secondary-main" as="h3" margin="none">
              What is your team investing in?
            </Text>
            <Text body as="p" margin="none">
              This distribution is by asset count, not by total value of assets.
            </Text>
          </CardHeading>
          <CardContent>
            <div style={{ minHeight: '500px', width: '100%' }}>
            <RadarChart
              height="large"
              data={buildRadarChartDataProp(props.assetTypeDistribution)}
              options={buildRadarChartOptionsProp()}
            />
            </div>
          </CardContent>
          <CardFooter>
            <LearnMore
              buttonText="Why does asset type distribution matter?"
              heading="What is your team investing in?"
              subHeading=""
            >
              <Text body as="p">
                People tend to save up and invest their money in different places. It's a good strategy to diversify how you invest.
              </Text>
              <Text body as="p">
                Understanding this distribution as a company is important so that you can see if your team is taking additional action to securing their financial futures, apart from the company retirement funds.              </Text>
              <Text body as="p">
                It's also useful to understand that even though you might want to do a drive to push people for higher contributions to their retirement funds, they might already be investing elsewhere.              </Text>
            </LearnMore>
          </CardFooter>
        </Card>
      </Grid>
    </div>
  )
}
